import React from 'react';

import pluralize from 'pluralize';

import { Chip, Paper, Stack, Typography } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';

type ImportAction = 'created' | 'existing';

export interface ImportedRow {
  row_number: number;
  room_code: string;
  room_name: string;
  rate_code: string;
  rate_plan_name: string;
  room_type?: ImportAction;
  rate_plan?: ImportAction;
  room_rate?: ImportAction;
  duplicate_of?: number;
}

const CARD_SX = { flex: 1, p: 3, border: 1, borderColor: 'grey.200', borderRadius: 2 } as const;

const STATUS_COLUMNS: Array<[keyof ImportedRow, string]> = [
  ['room_type', 'Room'],
  ['rate_plan', 'Rate Plan'],
  ['room_rate', 'Room Rate'],
];

function statusCell(row: ImportedRow, field: keyof ImportedRow) {
  if (row.duplicate_of) {
    return (
      <Typography variant="body2" color="text.secondary">
        Duplicate of row {row.duplicate_of}
      </Typography>
    );
  }
  if (row[field] === 'created') {
    return <Chip label="New" color="success" variant="outlined" size="small" />;
  }
  return (
    <Typography variant="body2" color="text.secondary">
      Existing
    </Typography>
  );
}

const columns: Array<GridColDef<ImportedRow>> = [
  { field: 'row_number', headerName: 'Row', width: 70 },
  { field: 'room_code', headerName: 'Room Code', flex: 1, minWidth: 100 },
  { field: 'room_name', headerName: 'Room Name', flex: 1, minWidth: 150 },
  { field: 'rate_code', headerName: 'Rate Code', flex: 1, minWidth: 120 },
  { field: 'rate_plan_name', headerName: 'Rate Plan Name', flex: 1, minWidth: 150 },
  ...STATUS_COLUMNS.map(
    ([field, headerName], index): GridColDef<ImportedRow> => ({
      field,
      headerName,
      width: 110,
      align: 'center',
      headerAlign: 'center',
      colSpan: (_value, row) => (row.duplicate_of && index === 0 ? STATUS_COLUMNS.length : 1),
      renderCell: ({ row }) => statusCell(row, field),
    }),
  ),
];

const countCreated = (rows: Array<ImportedRow>, action: keyof ImportedRow, key: (row: ImportedRow) => string) =>
  new Set(rows.filter((row) => row[action] === 'created').map(key)).size;

interface Props {
  rows: Array<ImportedRow>;
}

export default function PropertyImportResults({ rows }: Props) {
  const cards = [
    { label: 'Rows Imported', value: rows.filter((row) => !row.duplicate_of).length },
    { label: 'Rooms Created', value: countCreated(rows, 'room_type', (row) => row.room_code) },
    { label: 'Rate Plans Created', value: countCreated(rows, 'rate_plan', (row) => row.rate_code) },
    {
      label: 'Room Rates Created',
      value: countCreated(rows, 'room_rate', (row) => `${row.room_code}/${row.rate_code}`),
    },
  ];

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={2}>
        {cards.map(({ label, value }) => (
          <Paper key={label} elevation={0} sx={CARD_SX}>
            <Typography variant="body2" color="text.secondary" mb={1}>
              {label}
            </Typography>
            <Typography variant="h4" fontWeight={600} color="success.main">
              {value}
            </Typography>
          </Paper>
        ))}
      </Stack>
      <Typography variant="h6">Uploaded Rows ({pluralize('row', rows.length, true)})</Typography>
      <Paper elevation={0} sx={{ border: 1, borderColor: 'grey.200', borderRadius: 2 }}>
        <DataGrid
          rows={rows.map((row) => ({ id: row.row_number, ...row }))}
          columns={columns}
          initialState={{
            pagination: { paginationModel: { pageSize: 25 } },
          }}
          pageSizeOptions={[10, 25, 50, 100]}
          disableRowSelectionOnClick
          disableColumnFilter
          disableColumnSelector
          disableColumnMenu
          disableColumnSorting
          disableDensitySelector
          disableColumnResize
          autoHeight
          getRowHeight={() => 'auto'}
          sx={{ border: 0, '& .MuiDataGrid-cell': { display: 'flex', alignItems: 'center', py: 1 } }}
        />
      </Paper>
    </Stack>
  );
}
